import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiErrorDetail } from "./api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  // null = checking, false = anon, object = authenticated
  const [user, setUser] = useState(null);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("ablemp_token");
    if (!token) {
      setUser(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      localStorage.setItem("ablemp_user", JSON.stringify(data));
      setUser(data);
    } catch (_e) {
      localStorage.removeItem("ablemp_token");
      localStorage.removeItem("ablemp_user");
      setUser(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (identifier, password) => {
    try {
      const { data } = await api.post("/auth/login", { identifier, password });
      localStorage.setItem("ablemp_token", data.token);
      localStorage.setItem("ablemp_user", JSON.stringify(data.user));
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return {
        ok: false,
        error: formatApiErrorDetail(e.response?.data?.detail) || e.message,
      };
    }
  }, []);

  const register = useCallback(async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      // Register now returns {token, user} so we can use the token for onboarding uploads
      if (data?.token) {
        localStorage.setItem("ablemp_token", data.token);
        localStorage.setItem("ablemp_user", JSON.stringify(data.user));
        setUser(data.user);
      }
      return { ok: true, user: data.user || data, token: data?.token };
    } catch (e) {
      return {
        ok: false,
        error: formatApiErrorDetail(e.response?.data?.detail) || e.message,
      };
    }
  }, []);

  const logout = useCallback(async () => {
    // Best-effort: revoke the JWT on the backend (adds jti to denylist).
    // Must run BEFORE clearing localStorage so the axios interceptor still
    // attaches the Authorization header.
    try {
      await api.post("/auth/logout");
    } catch (_) {
      /* ignore — proceed with client-side cleanup regardless */
    }
    localStorage.removeItem("ablemp_token");
    localStorage.removeItem("ablemp_user");
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, login, register, logout, refresh }),
    [user, login, register, logout, refresh]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
};
